'use client';
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { THREAD_METADATA_QUERY_KEY, setThreadMetadataInCache, } from '#pplx/frontend/apps/ask/all-results/hooks/allResultsQuery.js';
import { allowsLocalComputerTask, useAsiAccessDecision, useRefreshAsiAccessDecisionAfterFreeTurnUse, } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useAsiAccessDecision.js';
import { useCanUseComputer } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useCanUseComputer.js';
import { useViewerComputerRestriction } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useViewerComputerRestriction.js';
import { markComputerDashboardGraphQLCacheStale } from '#pplx/frontend/apps/ask/hooks/computer-hooks/computerDashboardGraphQLCacheState.js';
import { useComputerPaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useComputerPaywallUpsell.js';
import { useRefuseViewerSubmission } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useRefuseViewerSubmission.js';
import { consumeQFillAttribution } from '#pplx/frontend/apps/ask/hooks/query-fill-params-hooks/qfillAttribution.js';
import { useNativeBridgeSelector } from '#pplx/frontend/apps/ask/native-bridges/store.js';
import { clearComputerTaskLogicalTiming, markComputerTaskCreated, startComputerTaskLogicalTiming, } from '#pplx/frontend/apps/ask/o11y/computerTaskTiming.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useResultsActions } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { LOCAL_MODEL_PREFERENCE } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { useLocalUserSettings } from '#pplx/frontend/apps/ask/user-settings/getLocalUserSettings.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useFeatureFlags } from '#pplx/frontend/experimentation/hooks.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
const DEFAULT_BASE_PATH = '/computer/tasks';
const ComputerUpgradeModalChooser = Dynamic(async () => {
    const { ComputerUpgradeModalChooser } = await import('#pplx/frontend/apps/ask/hooks/computer-before-submit/ComputerUpgradeModalChooser.js');
    return { default: ComputerUpgradeModalChooser };
}, {});
export const useComputerTaskSubmitter = ({ basePath, modalOrigin, navigateLabel, onBeforeNavigate, onCannotUseComputerOverride, searchModel, targetCollection, beforeSubmit, onTaskCreated, onFirstMessageReceived: onFirstMessageReceivedArg, getTaskUrl, targetThreadAccessLevel, }) => {
    const { submitQuery } = usePerplexityQueryState();
    const { askDeepResearchModel } = useThreadConfig();
    const { clear: clearResults } = useResultsActions();
    const router = useRouter();
    const canUseComputer = useCanUseComputer();
    const { data: accessDecision } = useAsiAccessDecision();
    const viewerRestriction = useViewerComputerRestriction();
    const refuseViewerSubmission = useRefuseViewerSubmission(viewerRestriction);
    const refreshAccessDecisionAfterFreeTurnUse = useRefreshAsiAccessDecisionAfterFreeTurnUse();
    const { isEnterprise } = useIsSubscribed();
    const { openModal } = useModalContext();
    const openComputerPaywallUpsell = useComputerPaywallUpsell();
    const queryClient = useQueryClient();
    const { getFlagValue } = useFeatureFlags();
    const isLocalBrowserAvailable = useNativeBridgeSelector((state) => state.isLocalBrowserAvailable);
    const [, setLastSubmittedAskInputMode] = useLocalUserSettings('lastSubmittedAskInputMode', undefined);
    const { getDefaultModelForMode } = useSearchModels({
        reason: 'computer-task-submitter-default-model',
    });
    const defaultComputerSearchModel = getDefaultModelForMode(SearchMode.ASI);
    const effectiveSearchModel = searchModel ?? defaultComputerSearchModel;
    const basePathRef = useRef(basePath ?? DEFAULT_BASE_PATH);
    basePathRef.current = basePath ?? DEFAULT_BASE_PATH;
    const onBeforeNavigateRef = useRef(onBeforeNavigate);
    onBeforeNavigateRef.current = onBeforeNavigate;
    const onCannotUseComputerOverrideRef = useRef(onCannotUseComputerOverride);
    onCannotUseComputerOverrideRef.current = onCannotUseComputerOverride;
    const searchModelRef = useRef(effectiveSearchModel);
    searchModelRef.current = effectiveSearchModel;
    const targetCollectionRef = useRef(targetCollection);
    targetCollectionRef.current = targetCollection;
    const targetThreadAccessLevelRef = useRef(targetThreadAccessLevel);
    targetThreadAccessLevelRef.current = targetThreadAccessLevel;
    const getTaskUrlRef = useRef(getTaskUrl);
    getTaskUrlRef.current = getTaskUrl;
    const onTaskCreatedRef = useRef(onTaskCreated);
    onTaskCreatedRef.current = onTaskCreated;
    const onFirstMessageReceivedRef = useRef(onFirstMessageReceivedArg);
    onFirstMessageReceivedRef.current = onFirstMessageReceivedArg;
    const submitComputerTask = useCallback((params) => {
        const query = params.query?.trim();
        if (!query) {
            return { sessionStarted: false, blockReason: 'empty-query' };
        }
        if (beforeSubmit?.()) {
            return { sessionStarted: false, blockReason: 'before-submit-blocked' };
        }
        if (refuseViewerSubmission()) {
            return { sessionStarted: false, blockReason: 'before-submit-blocked' };
        }
        const isLocalTask = (params.modelPreferenceOverride ?? searchModelRef.current) === LOCAL_MODEL_PREFERENCE;
        if (!canUseComputer && !(isLocalTask && allowsLocalComputerTask(accessDecision))) {
            if (onCannotUseComputerOverrideRef.current) {
                onCannotUseComputerOverrideRef.current();
                return { sessionStarted: false, blockReason: 'paywall-blocked' };
            }
            // Enterprise users keep the existing direct-to-paywall flow (business
            // pricing). Consumer Free users land on the educational modal first
            // (with an "Upgrade to Pro" CTA) instead of jumping straight to the paywall.
            if (isEnterprise) {
                openComputerPaywallUpsell({ origin: modalOrigin });
            }
            else {
                openModal(ComputerUpgradeModalChooser, { origin: modalOrigin }, { replace: true });
            }
            return { sessionStarted: false, blockReason: 'paywall-blocked' };
        }
        // support navless task creation via surfaces like voice
        const shouldNavigate = params.navigate !== false;
        const newFrontendContextUUID = params.newFrontendContextUUID ?? safeRandomUUID();
        if (params.workspaceDirectories !== undefined) {
            setThreadMetadataInCache(queryClient, newFrontendContextUUID, {
                local_workspace_directories: params.workspaceDirectories.map((d) => d.path),
            });
        }
        // Consume upsell attribution passed via URL params (e.g. from a computer_related_query upsell)
        const qfillRelatedQueryUUID = consumeQFillAttribution();
        const relatedQueryUUID = params.relatedQueryUUID ?? qfillRelatedQueryUUID ?? undefined;
        const isRelatedQuery = params.isRelatedQuery || relatedQueryUUID != null;
        if (shouldNavigate) {
            clearResults();
        }
        startComputerTaskLogicalTiming({
            frontendContextUuid: newFrontendContextUUID,
            querySource: params.querySource,
        });
        onTaskCreatedRef.current?.({
            frontend_context_uuid: newFrontendContextUUID,
            query,
        });
        markComputerTaskCreated({
            frontendContextUuid: newFrontendContextUUID,
            querySource: params.querySource,
        });
        const sessionStarted = submitQuery({
            rawQuery: query,
            branchMode: params.sourceBranch,
            fork: params.sourceBranch != null,
            dslQuery: params.dslQuery,
            mentions: params.mentions,
            prefills: params.prefills,
            attachments: params.attachments,
            queryDisplayMetadata: params.queryDisplayMetadata,
            collection: params.targetCollection !== undefined
                ? params.targetCollection
                : (targetCollectionRef.current ?? null),
            targetThreadAccessLevelOverride: targetThreadAccessLevelRef.current,
            newFrontendContextUUID,
            lastBackendUUIDOverride: params.sourceBranch?.sourceEntryUUID,
            promptSource: params.promptSource ?? 'user',
            querySource: params.querySource,
            timeFromFirstType: params.timeFromFirstType,
            modelPreferenceOverride: params.modelPreferenceOverride ?? searchModelRef.current,
            modeConfig: params.modeConfig,
            browserAgentAllowOnceFromToggle: params.browserAgentAllowOnceFromToggle,
            forceEnableBrowserAgent: params.forceEnableBrowserAgent,
            shouldRedirectToBackendUUID: false,
            frontendUUID: newFrontendContextUUID,
            canonicalPageContext: params.canonicalPageContext,
            incognitoOverride: params.incognitoOverride,
            localWorkspaceDirectories: params.workspaceDirectories?.map((d) => d.path),
            localFileGrants: params.localFileGrants,
            sourcesOverride: params.sourcesOverride,
            isLocalBrowserAvailable,
            workflowInstructions: params.workflowInstructions,
            workflowConfig: params.workflowConfig,
            workflowKey: params.workflowKey,
            sourceMetadata: params.sourceMetadata,
            searchModeOverride: SearchMode.ASI,
            shouldSetCurrentStreamId: shouldNavigate || params.showStreamWithoutNavigating === true,
            ...(isRelatedQuery && {
                isRelatedQuery: true,
                ...(relatedQueryUUID != null && {
                    relatedQueryUUID,
                }),
            }),
            onFirstMessageReceived: (msg) => {
                onFirstMessageReceivedRef.current?.({
                    frontend_context_uuid: newFrontendContextUUID,
                    slug: msg.slug,
                    context_uuid: msg.context_uuid,
                    backend_uuid: msg.backend_uuid,
                });
                params.onFirstMessageReceived?.(msg);
            },
            onThreadSlugReceived: (slug) => {
                const uuidKey = QueryClientHelper.makeQueryKey(THREAD_METADATA_QUERY_KEY, newFrontendContextUUID);
                const cached = queryClient.getQueryData(uuidKey);
                if (cached && slug !== newFrontendContextUUID) {
                    setThreadMetadataInCache(queryClient, slug, cached);
                    queryClient.removeQueries({ queryKey: uuidKey, exact: true });
                }
                params.onThreadSlugReceived?.(slug);
                if (!shouldNavigate)
                    return;
                const pathname = window.location.pathname;
                const activePath = params.basePath ?? basePathRef.current;
                const lastSegment = pathname.split('/').pop();
                // Only rewrite this submission's interstitial URL. The user may have opened
                // another task before this slug arrived.
                if (pathname.startsWith(`${router.base}${activePath}/`) &&
                    validateUuid(lastSegment) &&
                    lastSegment === newFrontendContextUUID) {
                    router.replace(params.basePath !== undefined
                        ? `${activePath}/${slug}`
                        : (getTaskUrlRef.current?.(slug) ?? `${activePath}/${slug}`), navigateLabel);
                }
            },
        });
        if (!sessionStarted) {
            clearComputerTaskLogicalTiming(newFrontendContextUUID);
            return {
                sessionStarted: false,
                blockReason: 'submit-blocked',
                frontendUUID: newFrontendContextUUID,
            };
        }
        const preserveHomepageMode = params.fromUpsell &&
            getFlagValue({
                flag: 'computer-upsell-preserve-homepage-mode',
                defaultValue: false,
            });
        if (!preserveHomepageMode) {
            setLastSubmittedAskInputMode(askDeepResearchModel ? 'search' : 'computer');
        }
        // The new task must appear on the next Computer dashboard visit instead
        // of being served from the cached preloaded query.
        markComputerDashboardGraphQLCacheStale();
        refreshAccessDecisionAfterFreeTurnUse();
        if (shouldNavigate) {
            const targetPath = params.basePath !== undefined
                ? `${params.basePath}/${newFrontendContextUUID}`
                : (getTaskUrlRef.current?.(newFrontendContextUUID) ??
                    `${basePathRef.current}/${newFrontendContextUUID}`);
            // Submitting from a transient/starter thread means the page is already
            // at the target URL — pushing it again would add a duplicate history
            // entry that the later thread-slug replace only partially rewrites,
            // leaving back-navigation on a stale alias of the same thread.
            if (window.location.pathname !== `${router.base}${targetPath}`) {
                onBeforeNavigateRef.current?.();
                router.push(targetPath, undefined, navigateLabel);
            }
        }
        return { sessionStarted: true, frontendUUID: newFrontendContextUUID };
    }, [
        clearResults,
        submitQuery,
        router,
        canUseComputer,
        accessDecision,
        refuseViewerSubmission,
        isEnterprise,
        openModal,
        openComputerPaywallUpsell,
        modalOrigin,
        beforeSubmit,
        navigateLabel,
        queryClient,
        getFlagValue,
        isLocalBrowserAvailable,
        setLastSubmittedAskInputMode,
        askDeepResearchModel,
        refreshAccessDecisionAfterFreeTurnUse,
    ]);
    const submitComputerFollowup = useCallback((params) => {
        const query = params.query?.trim();
        if (!query)
            return false;
        const effectiveSearchMode = params.searchModeOverride ?? SearchMode.ASI;
        if (effectiveSearchMode === SearchMode.ASI && refuseViewerSubmission()) {
            return false;
        }
        // only apply the ASI model default when the mode is ASI — other modes
        // use their own model routing and should not have a model forced on them.
        const effectiveModelOverride = params.modelPreferenceOverride ??
            (effectiveSearchMode === SearchMode.ASI ? searchModelRef.current : undefined);
        const submitted = submitQuery({
            rawQuery: query,
            attachments: params.attachments,
            mentions: params.mentions,
            prefills: params.prefills,
            sourcesOverride: params.sourcesOverride,
            collection: params.collection ?? null,
            newFrontendContextUUID: null,
            existingFrontendContextUUID: params.contextUuid,
            lastBackendUUIDOverride: params.lastBackendUUID,
            parentContextUUIDOverride: params.parentContextUUID,
            shouldSetCurrentStreamId: params.shouldSetCurrentStreamId,
            queryDisplayMetadata: params.queryDisplayMetadata,
            promptSource: 'user',
            querySource: 'computer-followup',
            modelPreferenceOverride: effectiveModelOverride,
            modeConfig: params.modeConfig,
            searchModeOverride: effectiveSearchMode,
            shouldRedirectToBackendUUID: false,
            frontendUUID: safeRandomUUID(),
            isLocalBrowserAvailable,
            incognitoOverride: params.incognitoOverride,
            onFirstMessageReceived: params.onFirstMessageReceived,
        });
        if (submitted) {
            refreshAccessDecisionAfterFreeTurnUse();
        }
        return submitted;
    }, [
        submitQuery,
        isLocalBrowserAvailable,
        refreshAccessDecisionAfterFreeTurnUse,
        refuseViewerSubmission,
    ]);
    return { submitComputerTask, submitComputerFollowup };
};
//# sourceMappingURL=useComputerTaskSubmitter.js.map